import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AuthenticationService } from '@app/core/authentication/authentication.service';
import { BillingFacade, PLANS_MAP } from '@app/shared/+state/billing';
import { LoadingState, NotificationType, User } from '@app/shared/interfaces';
import { BillingService } from '@app/shared/services/billing/billing.service';
import { DeviceDetectionService } from '@app/shared/services/device-detection/device-detection.service';
import { FairmintInvestmentStatus, FairmintService } from '@app/shared/services/fairmint/fairmint.service';
import { OptimizelyService } from '@app/shared/services/optimizely/optimizely.service';
import { ToastNotificationsService } from '@app/shared/services/toast-notifications/toast-notifications.service';
import { differenceInDays } from 'date-fns';
import { combineLatest, ReplaySubject } from 'rxjs';
import { filter, map, takeUntil, tap } from 'rxjs/operators';

@Component({
  standalone: false,
  selector: 'sbnb-your-plan',
  templateUrl: './your-plan.component.html',
  styleUrls: ['./your-plan.component.scss'],
})
export class YourPlanComponent implements OnInit, OnDestroy {
  subscriptionColour: 'positive' | 'neutral' | 'negative' | '';
  heroTitle: string;
  public loading$ = this.billingFacade.subscriptionLoadingState$.pipe(map((state) => state === LoadingState.Pending));
  public subscriptionError$ = this.billingFacade.subscriptionLoadingState$.pipe(
    map((state) => state === LoadingState.Error)
  );
  user: User;
  resuming: boolean;
  legacy: boolean;
  reactivating: boolean;
  investmentStatus: FairmintInvestmentStatus;
  planBreakdownVisible = false;

  public isActiveLegacy$ = combineLatest([
    this.billingFacade.isOnProfessionalPlan$,
    this.billingFacade.isOnHostPlan$,
    this.billingFacade.isExpiredSubscription$,
  ]).pipe(
    map(([isOnProfessionalPlan, isOnHostPlan, isExpired]) => !isExpired && !isOnProfessionalPlan && !isOnHostPlan)
  );

  public canCancelSubscription$ = this.billingFacade.canCancelSubscription$;
  public canManageAddons$ = this.billingFacade.canManageAddons$;

  public data$ = combineLatest([
    this.billingFacade.subscription$,
    this.billingFacade.notExpiredCheck$,
    this.authService.user$,
  ]).pipe(
    filter(([sub]) => Boolean(sub)),
    map(([subscription, notExpired, user]) => ({
      subscription: {
        ...subscription,
        days_left: this.calcDaysLeft(subscription.trial_end),
      },
      notExpired,
      user,
    })),
    tap((data) => {
      this.setHeroAttributes(data.subscription);
    })
  );
  private destroyed$: ReplaySubject<boolean> = new ReplaySubject(1);
  public showPlans$ = this.route.queryParams.pipe(map((params) => params['showPlans']));

  constructor(
    private billingService: BillingService,
    private authService: AuthenticationService,
    private toast: ToastNotificationsService,
    private router: Router,
    private deviceDetectionService: DeviceDetectionService,
    private optimizelyService: OptimizelyService,
    private fairmint: FairmintService,
    private readonly billingFacade: BillingFacade,
    private route: ActivatedRoute
  ) {}

  ngOnInit() {
    this.billingFacade.abandonSubscriptionChange();

    // If Billing is disabled in Optimizely, redirect
    this.optimizelyService.billingDisabled$.pipe(takeUntil(this.destroyed$)).subscribe((disabled) => {
      if (disabled) {
        this.router.navigate(['/settings/subscription/unavailable']);
      }
    });

    if (this.deviceDetectionService.isIosApp()) {
      this.router.navigate(['/settings/subscription/denied']);
      return;
    }

    this.refreshState();
  }

  ngOnDestroy() {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  getInvestmentStatus() {
    this.fairmint.fetchInvestmentStatus().subscribe((res: FairmintInvestmentStatus) => {
      this.investmentStatus = res;
    });
  }

  resumeSubscription() {
    this.resuming = true;

    this.billingService.resumeSubscription().subscribe((res) => {
      this.resuming = false;

      if (res.error) {
        this.toast.open(`${res.message ? res.message : res.error_reason}`, 'Dismiss', NotificationType.Error, 999999);
      } else {
        this.refreshState();
      }
    });
  }

  reactivateSubscription() {
    this.reactivating = true;

    this.billingService.reactivateSubscription().subscribe((res) => {
      this.reactivating = false;

      if (res.error) {
        this.toast.open(`${res.message ? res.message : res.error_reason}`, 'Dismiss', NotificationType.Error, 999999);
      } else {
        this.refreshState();
      }
    });
  }

  calcDaysLeft(trialEnd) {
    return differenceInDays(trialEnd, new Date());
  }

  setHeroAttributes(subscription) {
    const currentPlan = subscription?.plan?.name ?? 'unknown';
    const planLabel = PLANS_MAP[currentPlan]?.name;
    switch (subscription.status) {
      case 'trialing':
        this.heroTitle = `You are trialing a ${planLabel} Plan subscription.`;
        this.subscriptionColour = 'neutral';
        break;

      case 'pending':
        this.heroTitle = `Your ${planLabel} Plan subscription is pending.`;
        this.subscriptionColour = 'positive';
        break;

      case 'active':
        this.heroTitle = `Your ${planLabel} Plan subscription is active.`;
        this.subscriptionColour = 'positive';
        break;

      case 'grace':
        this.heroTitle = `A Payment has failed for your ${planLabel} Plan.`;
        this.subscriptionColour = 'neutral';
        break;

      case 'paused':
        this.heroTitle = `Your ${planLabel} Plan subscription is paused.`;
        this.subscriptionColour = 'neutral';
        break;

      case 'ended':
        this.heroTitle = `Your ${planLabel} Plan trial has ended.`;
        this.subscriptionColour = 'negative';
        break;

      case 'cancelled':
        this.heroTitle = `Your ${planLabel} Plan subscription has ended.`;
        this.subscriptionColour = 'negative';
        break;

      case 'pending_cancellation':
        this.heroTitle = `Your ${planLabel} Plan subscription has been cancelled.`;
        this.subscriptionColour = 'neutral';
        break;

      default:
        this.heroTitle = 'Billing status unclear.';
        this.subscriptionColour = 'neutral';
        break;
    }
  }

  private refreshState() {
    this.billingFacade.initManageSubscription();
    this.getInvestmentStatus();
    this.user = this.authService.getUserDetails();
  }
}
